import DateFnsUtils from "@date-io/date-fns";
import { Box } from "@material-ui/core";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import esLocale from "date-fns/locale/es";
import { Form, Formik, FormikHelpers } from "formik";
import React, { Fragment, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory } from "react-router";
import styled from "styled-components";
import { apiCreateBankAccount } from "../../../../../../api/bankAccount/apiCreateBankAccount";
import { apiUpdateService } from "../../../../../../api/service/apiUpdateService";
import { apiCreateServiceHolder } from "../../../../../../api/serviceHolder/apiCreateServiceHolder";
import { apiUpdateServiceHolder } from "../../../../../../api/serviceHolder/apiUpdateServiceHolder";
import useSnacks from "../../../../../../components/hooks/useSnacks";
import ValuationProcess from "./components/ValuationProcess";
import useSaveAndValidateMutation from "../../../../../../mutations/service/useSaveAndValidateMutation";
import useServiceValuationFormOptions from "./hooks/useServiceValuationFormOptions";
import { prepareValuationTasks } from "./prepareValuationTasks";
import ServiceValuationForm from "./ServiceValuationForm";
import { transformServiceToFormData } from "./transformServiceToFormData";
import { TServiceValuationForm, TValuationFormTask } from "./types";
import useSaveAndExitMutation from "../../../../../../mutations/service/useSaveAndExitMutation";
import useServiceValuationFormValidation from "./hooks/useServiceValuationFormValidation";
import useCuideoAsistServiceValuationFormValidation from "./hooks/useCuideoAsistServiceValuationFormValidation";
import { TGetServicesCollectionItem } from "../../../../../../api/service/service.types";

const StyledWrapper = styled(Box)`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const StyledFormBody = styled(Box)`
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: scroll;
  box-sizing: border-box;
  padding: 0 1rem 1rem;

  ::-webkit-scrollbar {
    width: 0.375rem;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background: rgba(0, 72, 120, 0.25);
    width: 0.375rem;
    border-radius: 0.25rem;
  }
`;

const StyledFormActions = styled(Box)``;

interface IServiceValuationProps {
  service: TGetServicesCollectionItem;
  valuationNotes: string;
  pathologyOptions: { value: string; label: string }[];
}

const ServiceValuation = (props: IServiceValuationProps) => {
  const { service, valuationNotes, pathologyOptions } = props;
  const saveAndExitMutation = useSaveAndExitMutation();
  const saveAndValidateMutation = useSaveAndValidateMutation(
    apiCreateServiceHolder,
    apiUpdateServiceHolder,
    apiCreateBankAccount,
    apiUpdateService
  );
  const { snackSuccessMessage, snackErrorMessage } = useSnacks();
  const { formatMessage } = useIntl();
  const validationSchema = useServiceValuationFormValidation();
  const cuideoAsistValidationSchema =
    useCuideoAsistServiceValuationFormValidation();
  const history = useHistory();
  const {
    regionOptions,
    areaOptions,
    familiarUnitOptions,
    speechOptions,
    mobilityOptions,
    emotionalStatusOptions,
    careAdaptationOptions,
    sleepQualityOptions,
    toiletLevelOptions,
    salaryTypeOptions,
    officialDegreeOptions,
    serviceHolderUserStatusOptions,
    dependencyOptions,
  } = useServiceValuationFormOptions();
  const [state, setState] = useState({
    op: null as "save_and_exit" | "save_and_validate" | null,
    tasks: [] as TValuationFormTask[],
    initialValues: transformServiceToFormData(
      service,
      regionOptions,
      areaOptions,
      familiarUnitOptions,
      pathologyOptions,
      speechOptions,
      mobilityOptions,
      emotionalStatusOptions,
      careAdaptationOptions,
      sleepQualityOptions,
      toiletLevelOptions,
      salaryTypeOptions,
      officialDegreeOptions,
      serviceHolderUserStatusOptions,
      dependencyOptions
    ),
  });

  const handleFormSubmit = async (
    values: TServiceValuationForm,
    actions: FormikHelpers<TServiceValuationForm>
  ) => {
    setState((prevState) => ({
      ...prevState,
      op: "save_and_validate",
      tasks: [],
    }));
    const tasks = prepareValuationTasks(service.id, values);
    setState((prevState) => ({ ...prevState, op: "save_and_validate", tasks }));
    await saveAndValidateMutation.mutateAsync({
      serviceId: service.id,
      serviceHolderId: values.service_holder_id,
      tasks,
      onValuationProcessUpdate: (taskId, status, errors) => {
        setState((prevState) => ({
          ...prevState,
          op: prevState.op,
          tasks: prevState.tasks.map((task) => {
            if (task.id === taskId) {
              return { ...task, status, errors };
            }
            return task;
          }),
        }));
      },
      onMutationComplete: () => {
        setTimeout(() => {
          setState((prevState) => ({ ...prevState, op: null, tasks: [] }));
          if (history.action !== "POP") {
            history.goBack();
          } else {
            history.push("/services");
          }
        }, 1000);
      },
    });
  };

  const handleFormSaveAndExit = async (values: TServiceValuationForm) => {
    try {
      setState((prevState) => ({
        ...prevState,
        op: "save_and_exit",
        tasks: [],
      }));
      await saveAndExitMutation.mutateAsync({
        serviceId: service.id,
        formData: values,
        valuationNotes,
      });
      snackSuccessMessage(
        formatMessage({
          id: "ServiceValuation.SaveAndExitSuccess",
          defaultMessage: "Valoración guardada",
        })
      );
      setState((prevState) => ({ ...prevState, op: null, tasks: [] }));
      if (history.action !== "POP") {
        history.goBack();
      } else {
        history.push("/services");
      }
    } catch (e) {
      snackErrorMessage(
        formatMessage({
          id: "ServiceValuation.SaveAndExitError",
          defaultMessage: "Error al guardar valoración",
        })
      );
    }
  };

  return (
    <Fragment>
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={state.initialValues}
        validationSchema={
          service.Tipo_de_Servicio === "Cuideo Asist"
            ? cuideoAsistValidationSchema
            : validationSchema
        }
        validateOnMount
      >
        <Form style={{ height: "100%" }}>
          <MuiPickersUtilsProvider utils={DateFnsUtils} locale={esLocale}>
            <StyledWrapper>
              <StyledFormBody>
                <ServiceValuationForm
                  service={service}
                  pathologyOptions={pathologyOptions}
                  regionOptions={regionOptions}
                  areaOptions={areaOptions}
                  familiarUnitOptions={familiarUnitOptions}
                  speechOptions={speechOptions}
                  mobilityOptions={mobilityOptions}
                  emotionalStatusOptions={emotionalStatusOptions}
                  careAdaptationOptions={careAdaptationOptions}
                  sleepQualityOptions={sleepQualityOptions}
                  toiletLevelOptions={toiletLevelOptions}
                  salaryTypeOptions={salaryTypeOptions}
                  officialDegreeOptions={officialDegreeOptions}
                  serviceHolderUserStatusOptions={
                    serviceHolderUserStatusOptions
                  }
                  dependencyOptions={dependencyOptions}
                  onSaveAndExit={handleFormSaveAndExit}
                />
              </StyledFormBody>
              <StyledFormActions></StyledFormActions>
            </StyledWrapper>
          </MuiPickersUtilsProvider>
        </Form>
      </Formik>
      <ValuationProcess
        open={state.op === "save_and_validate"}
        tasks={state.tasks}
      />
    </Fragment>
  );
};

export default ServiceValuation;
